import React, { Component } from 'react';
import {
    Typography,
    TextField,
    Button,
    FormControl,
    Input,
    InputLabel,
    InputAdornment,
    IconButton,
    Paper
} from '@material-ui/core';
import { Visibility, VisibilityOff } from '@material-ui/icons';
import { changeSubscriptionForGateway } from '../Networking/APIClient';
import Utilities from '../Utilities/Utilities';

const buttonContainerStyle = {
    marginRight: 10,
    float: 'right',
    paddingTop: 20,
    paddingBottom: 20
}

const buttonStyle = {
    width: 100,
    marginLeft: 20,
};

const passwordStyle = {
    width: '100%',
    marginTop: 16,
    marginBottom: 8,
}

const passwordHelperTextStyle = {
    marginTop: 5,
    color: '#f44336',
    padding: 0,
}

let self;
let t;

// This page is reached when the customer clicks on the 'Change' button underneath a subscription.
// For the button to change from 'Choose' to 'Change', the subscription has to fulfill certain requirements.
// One of which being the current subscription needs to have a 'subscription_valid_to_date' of more than 7 days from the current date.
class ChangeSubscription extends Component {
    constructor(props) {
        super(props);

        self = this;
        t = props.t;

        this.state = {
            email: { value: '', error: false, helperText: '' },
            password: { value: '', error: false, helperText: '', showPassword: false },
            currentPageState: 'before',
            success: null,
        };
    }

    // When subscription is successfully changed, the button that says 'Finished' updates AppData and returns the customer to the account overview. 
    onFinishClicked = e => {
        e.preventDefault();

        self.props.updateAppData();
        self.props.updatePage('account_overview');
        return;
    }

    // Upon failure of subscription change, the button says 'OK' and returns the customer to the 'before' page state to attempt a retry.
    onCancelClicked = (e) => {
        e.preventDefault();

        this.setState({
            currentPageState: 'before',
            success: null,
            email: {
                value: this.state.email.value,
                error: false,
                helperText: ''
            },
            password: {
                value: '',
                error: false,
                helperText: '',
                showPassword: false
            }
        });
    };


    handleChange = (field) => (event) => {
        this.setState({
            [field]: {
                ...this.state[field],
                value: event.target.value,
                error: false,
                helperText: ''
            }
        });
    };

    handleClickShowPassword = () => {
        this.setState((prevState) => ({
            password: {
                ...prevState.password,
                showPassword: !prevState.password.showPassword
            }
        }));
    };

    handleMouseDownPassword = (event) => {
        event.preventDefault();
    };

    // This method is needed so that the soft keyboard of phones closes
    // when the "submit" button of the soft keyboard is clicked
    // on the last text field
    onKeyPressed = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            document.activeElement.blur();
        }
    }

    // Performs initial checks of the form before sending the request.
    // Also handles the result from the method that in turn calls the APIClient method.
    // Handles the page state accordingly.
    onInitialChangeSubscriptionClicked = async (e) => {
        e.preventDefault();
        const { email, password } = this.state;

        if (!email.value) {
            this.setState({ email: { ...email, error: true, helperText: t('user_email_or_password_wrong') } });
            return;
        }

        if (!password.value) {
            this.setState({ password: { ...password, error: true, helperText: t('user_email_or_password_wrong') } });
            return;
        }

        try {
            const result = await this.onExecuteChangeSubscriptionClicked(
                self.props.selectedSubscription.subscription_type,
                e,
                { email: email.value, password: password.value }
            );

            if (result.httpStatus === 401) {
                this.setState({
                    email: { ...email, error: true, helperText: t('user_email_or_password_wrong') },
                    password: { ...password, error: true, helperText: t('user_email_or_password_wrong') }
                });
                return;
            }

            switch (result.status) {
                case 'Success':
                    this.setState({ currentPageState: 'after', success: true });
                    break;

                case 'GatewaySubscriptionDateInvalid':
                case 'GatewaySubscriptionExpiresTooSoon':
                case 'GatewaySubscriptionInvalid':
                case 'InvalidNewSubscriptionType':
                case 'SubscriptionChangeFailed':
                case 'SubscriptionChangeThrewException':
                default:
                    this.setState({ currentPageState: 'after', success: false });
                    break;
            }

        } catch (err) {
            console.error("An unexpected error occurred:", err);
            this.setState({
                currentPageState: 'after',
                success: false
            });
        }
    };

    // Forwards the request to change the subscription type for the gateway to the APIClient.
    // Returns the status and whether the request was a success.
    onExecuteChangeSubscriptionClicked = async (new_subscription_type, e, credentials) => {
        e.preventDefault();
        self.props.setLoading(true);
        const selectedSubscription = self.props.selectedSubscription;

        try {
            const result = await changeSubscriptionForGateway(
                credentials,
                selectedSubscription.value_code,
                new_subscription_type
            );
            const knownClientErrors = [
                'GatewaySubscriptionDateInvalid',
                'GatewaySubscriptionExpiresTooSoon',
                'GatewaySubscriptionInvalid',
                'InvalidNewSubscriptionType',
                'SubscriptionChangeFailed'
            ];

            if (result.status === 'Success') {
                return { success: true, status: 'Success' };
            }

            if (knownClientErrors.includes(result.status)) {
                return { success: false, status: result.status };
            }

            return { success: false, status: 'SubscriptionChangeThrewException' };
        } catch (error) {
            console.error("Change subscription error:", error);
            return { success: false, status: 500 };
        } finally {
            self.props.setLoading(false);
        }
    };

    render() {
        let content;

        switch (this.state.currentPageState) {
            case 'before':
                content =
                    <div className="content-container">
                        <div className="typography-title-container">
                            <Typography variant="title">
                                {t('subscription_change_dialog_title')}
                            </Typography>
                        </div>
                        <div className="typography-title-container">
                            <Typography variant="subheading">
                                {t('subscription_change_dialog_message_1')}
                            </Typography>
                        </div>
                        <form>
                            {/* Input customer email, for authentication purposes */}
                            <TextField style={editTextStyle}
                                error={this.state.email.error}
                                helperText={this.state.email.helperText}
                                id="email"
                                value={this.state.email.value}
                                label={t('my_email')}
                                onChange={this.handleChange('email')}
                                onKeyPress={e => this.onKeyPressed(e)}
                                margin="normal" />
                            {/* Input customer password, for authentication purposes */}
                            <FormControl style={passwordStyle}>
                                <InputLabel htmlFor="password">{t('password')}</InputLabel>
                                <Input
                                    id="password"
                                    type={this.state.password.showPassword ? 'text' : 'password'}
                                    value={this.state.password.value}
                                    error={this.state.password.error}
                                    /* This is a hint, which browsers are not required to comply with.
                                    However modern browsers have stopped autofilling <input> elements with autocomplete="new-password" for this very reason. */
                                    autoComplete="new-password"
                                    onChange={this.handleChange('password')}
                                    onKeyPress={e => this.onKeyPressed(e)}
                                    endAdornment={
                                        <InputAdornment position="end">
                                            <IconButton
                                                aria-label="Toggle password visibility"
                                                onClick={this.handleClickShowPassword}
                                                onMouseDown={this.handleMouseDownPassword}>
                                                {this.state.password.showPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    }
                                />
                            </FormControl>
                        </form>
                        <div style={buttonContainerStyle}>
                            <Button
                                style={buttonStyle}
                                variant="contained"
                                color="primary"
                                disabled={this.props.loading}
                                onClick={e => this.onInitialChangeSubscriptionClicked(e)}>
                                {this.props.loading ? t('loading') : t('change')}
                            </Button >
                        </div>
                    </div>;
                break;
            case 'after':
                content = (
                    <div className="content-container">
                        <div>
                            <div className="typography-title-container">
                                <Typography variant="title" color={this.state.success ? 'default' : 'error'}>
                                    {this.state.success
                                        ? t('subscription_change_successful_dialog_title')
                                        : t('subscription_change_failed_dialog_title')}
                                </Typography>
                            </div>
                            <div>
                                <Typography variant="subheading">
                                    {this.state.success ? (
                                        <React.Fragment>
                                            {Utilities.replaceBrackets(
                                                t('subscription_change_successful_dialog_message_1'),
                                                this.props.currentSubscriptionType,
                                                this.props.selectedSubscription.subscription_type,
                                                this.props.selectedSubscription.valid_to_date
                                            )}
                                            <br />
                                            <br />
                                            {t('subscription_change_successful_dialog_message_2')}
                                        </React.Fragment>
                                    ) : (
                                        t('subscription_change_failed_dialog_message')
                                    )}
                                </Typography>
                            </div>
                            <div style={buttonContainerStyle}>
                                <Button
                                    style={buttonStyle}
                                    variant="contained"
                                    color="primary"
                                    onClick={e => this.state.success ? this.onFinishClicked(e) : this.onCancelClicked(e)}
                                >
                                    {this.state.success ? t('finished') : t('ok')}
                                </Button>
                            </div>
                        </div>
                    </div>
                );
                break;
            default:
                break;
        }

        return (
            <div className="content-frame">
                <Paper className="content">
                    {!this.props.isLoading ? content : null}
                </Paper>
            </div>
        );
    }
}

const editTextStyle = {
    width: '100%',
}

export default ChangeSubscription;


// WEBPACK FOOTER //
// ./src/Pages/ChangeSubscription.js